class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = null;
    this.thumbnailSlider = null;
    this.resizeObserver = null;
    this.intersectionObserver = null;
    this.minuteurFilet = null;
    this.estMonte = false;
    this.maxPageWidth = NaN;
  }

  connectedCallback() {
    // 🔴 MONTAGE DIFFÉRÉ (26/08/2026). La page d'accueil porte DOUZE slider-component.
    // Ils se montaient tous d'un bloc au moment où le custom element était défini, et la
    // mesure PageSpeed attribuait alors ~400 ms de CPU à splide.js. Le coût ne se voyait pas
    // sur le TBT seul : le LCP se décomposait en 8 ms de TTFB, 145 ms d'attente, 168 ms de
    // téléchargement et 1 971 ms d'ATTENTE DU RENDU. L'image du héros arrivait en 320 ms et
    // patientait deux secondes que le main thread se libère.
    // On ne monte donc que ce qui est visible, le reste attend d'approcher de l'écran.
    // 🔑 Aucun décalage de mise en page possible : `slider.css` pose `visibility: hidden` sur
    // un `.splide` non initialisé, jamais `display: none`. L'espace est déjà réservé, un
    // montage tardif ne pousse rien.
    if (!('IntersectionObserver' in window)) {
      this.monte();
      return;
    }

    const cadre = this.getBoundingClientRect();
    const sansSurface = cadre.width === 0 || cadre.height === 0;

    // Visible ou juste sous la ligne de flottaison : on monte tout de suite, sans quoi on
    // échangerait du TBT contre un slider vide sous les yeux du visiteur.
    if (!sansSurface && cadre.top < window.innerHeight * 1.5 && cadre.bottom > -200) {
      this.monte();
      return;
    }

    // Un élément sans surface est dans un conteneur masqué, typiquement le tiroir panier.
    // Son IntersectionObserver ne se déclenchera qu'à l'ouverture du tiroir : on garde un
    // filet après `load` pour qu'aucun slider ne reste invisible si l'ouverture ne suffit pas.
    if (sansSurface) this.planifieFilet();

    this.intersectionObserver = new IntersectionObserver(
      (entrees) => {
        if (entrees.some((entree) => entree.isIntersecting)) this.monte();
      },
      { rootMargin: '400px 0px' },
    );
    this.intersectionObserver.observe(this);
  }

  // Le filet ne part qu'APRÈS `load`, donc hors du chemin critique : il ne rend pas le CPU
  // qu'on vient d'économiser sur le premier rendu.
  planifieFilet() {
    const lance = () => {
      if (this.estMonte) return;
      if ('requestIdleCallback' in window) {
        requestIdleCallback(
          () => {
            if (!this.estMonte) this.monte();
          },
          { timeout: 4000 },
        );
      } else {
        this.minuteurFilet = setTimeout(() => {
          if (!this.estMonte) this.monte();
        }, 2000);
      }
    };

    if (document.readyState === 'complete') lance();
    else window.addEventListener('load', lance, { once: true });
  }

  monte() {
    if (this.estMonte) return;
    this.estMonte = true;

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    if (this.minuteurFilet) {
      clearTimeout(this.minuteurFilet);
      this.minuteurFilet = null;
    }

    // Lu ici et non plus dans le constructeur : douze `getComputedStyle` d'affilée au
    // chargement forçaient autant de recalculs de mise en page avant le premier rendu.
    const section = this.closest('.shopify-section');
    this.maxPageWidth = section ? parseFloat(getComputedStyle(section).getPropertyValue('max-width')) : NaN;

    this.slider = this.initMainSlider();

    if (this.querySelector('[data-ref="thumbnail-slider"]')) {
      this.thumbnailSlider = this.initThumbnailSlider();
    }

    if (this.thumbnailSlider) {
      this.slider.sync(this.thumbnailSlider);
    }

    this.slider.mount();

    if (this.thumbnailSlider) {
      this.thumbnailSlider.mount();
    }

    if (!this.thumbnailSlider) {
      this.slider.on('mounted', () => {
        this.updateSize();
      });

      // Utiliser ResizeObserver pour détecter tous les changements de taille
      this.resizeObserver = new ResizeObserver(() => {
        this.updateSize();
      });
      this.resizeObserver.observe(document.documentElement);
    }
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    if (this.minuteurFilet) {
      clearTimeout(this.minuteurFilet);
      this.minuteurFilet = null;
    }
  }

  initMainSlider() {
    const perPageDesktop = this.getAttribute('data-per-page-desktop');
    let perPageMobile = this.getAttribute('data-per-page-mobile');
    const gap = this.getAttribute('data-gap') + 'px';
    const showArrows = this.getAttribute('data-show-arrows') === 'true';
    const showArrowsMobile = this.getAttribute('data-show-arrows-mobile') === 'true';
    const showPagination = this.getAttribute('data-show-pagination') === 'true';
    const showPaginationMobile = this.getAttribute('data-show-pagination-mobile') === 'true';
    const autoplay = this.getAttribute('data-autoplay') === 'true';
    const autoplaySpeed = this.getAttribute('data-autoplay-speed') + '000';
    const rewind = this.getAttribute('data-rewind') === 'true';

    if (perPageMobile === '1.8') perPageMobile = 1;

    return new Splide(this.querySelector('[data-ref="main-slider"]'), {
      perPage: perPageMobile,
      perMove: 1,
      rewind: rewind,
      gap: gap,
      drag: true,
      snap: true,
      arrows: showArrows,
      arrowsMobile: showArrowsMobile,
      pagination: showPaginationMobile,
      autoplay: autoplay,
      interval: autoplaySpeed,
      autoHeight: true,
      lazyLoad: false,
      flickMaxPages: 1,
      flickPower: 400,
      mediaQuery: 'min',
      breakpoints: {
        750: {
          perPage: perPageDesktop,
          pagination: showPagination,
          arrows: showArrows,
        },
      },
    });
  }

  initThumbnailSlider() {
    const showArrows = this.getAttribute('data-show-arrows-thumbnails') === 'true';

    return new Splide(this.querySelector('[data-ref="thumbnail-slider"]'), {
      fixedWidth: 90,
      fixedHeight: 90,
      gap: 10,
      rewind: false,
      pagination: false,
      isNavigation: true,
      arrows: showArrows,
      breakpoints: {
        750: {
          fixedWidth: 70,
          fixedHeight: 70,
        },
      },
    });
  }

  updateSize() {
    if (!this.slider) return;

    this.pageWidth = document.documentElement.clientWidth;
    const section = this.closest('.shopify-section');
    if (!section) return;
    this.sectionPadding = parseFloat(getComputedStyle(section).getPropertyValue('padding-right'));

    var padding = this.sectionPadding;
    if (this.pageWidth > this.maxPageWidth) {
      padding = (this.pageWidth - this.maxPageWidth) / 2 + this.sectionPadding;
    }

    this.style.width = `calc(100% + ${2 * padding}px)`;
    this.style.marginLeft = `-${padding}px`;
    this.style.marginRight = `-${padding}px`;

    if (this.querySelector('.splide__track')) {
      this.querySelector('.splide__track').style.paddingRight = `${padding}px`;
      this.querySelector('.splide__track').style.paddingLeft = `${padding}px`;
    }

    if (this.querySelector('.splide__arrow--prev') && this.querySelector('.splide__arrow--next')) {
      this.querySelector('.splide__arrow--prev').style.left = `${padding + 10}px`;
      this.querySelector('.splide__arrow--next').style.right = `${padding + 10}px`;
    }
  }

  // ⚠️ Cette méthode MASQUE `Element.remove()`. `cross-sell.js` l'appelle avec un index pour
  // retirer une diapositive, mais tout code qui ferait `element.remove()` pour sortir le
  // slider du DOM tomberait ici aussi. On distingue donc les deux, et on monte au besoin :
  // depuis le montage différé, `this.slider` peut être encore nul.
  remove(index) {
    if (typeof index !== 'number') {
      super.remove();
      return;
    }
    if (!this.estMonte) this.monte();
    if (this.slider) this.slider.remove(index);
  }
}

if (!customElements.get('slider-component')) {
  customElements.define('slider-component', SliderComponent);
}
